import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { webStorage } from '@/lib/webStorage';

export type ThemePreference = 'system' | 'light' | 'dark';
/** Mirrors AppLanguage in @/i18n (kept literal here to avoid an import cycle). */
export type LanguagePreference = 'en' | 'ar' | 'fr' | null;

// Local device preferences, persisted to disk (AsyncStorage) so they survive
// app restarts. `themePreference` drives the app-wide light/dark toggle (see
// @/theme/ThemeProvider); 'system' follows the OS appearance setting.
// `language` null means no choice was made on this device yet — the app follows
// the device locale (and adopts the server profile language once on sign-in).
type SettingsState = {
  pushNotifications: boolean;
  themePreference: ThemePreference;
  language: LanguagePreference;
  biometricLock: boolean;
  setPushNotifications: (v: boolean) => void;
  setThemePreference: (v: ThemePreference) => void;
  setLanguage: (v: LanguagePreference) => void;
  setBiometricLock: (v: boolean) => void;
  /** True once the persisted state has been read from disk. Gates first paint so a
   *  pinned Light/Dark preference never flashes as "System" for a frame on launch
   *  (and the language/RTL never flashes as the device default). */
  _hasHydrated: boolean;
  _setHasHydrated: (v: boolean) => void;
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      pushNotifications: true,
      themePreference: 'system',
      language: null,
      biometricLock: false,
      setPushNotifications: (v) => set({ pushNotifications: v }),
      setThemePreference: (v) => set({ themePreference: v }),
      setLanguage: (v) => set({ language: v }),
      setBiometricLock: (v) => set({ biometricLock: v }),
      _hasHydrated: false,
      _setHasHydrated: (v) => set({ _hasHydrated: v }),
    }),
    {
      name: 'sagesse.settings.v1',
      storage: createJSONStorage(() => webStorage),
      partialize: (s) => ({
        pushNotifications: s.pushNotifications,
        themePreference: s.themePreference,
        language: s.language,
        biometricLock: s.biometricLock,
      }),
      onRehydrateStorage: () => (state) => {
        state?._setHasHydrated(true);
      },
    },
  ),
);
